import type { Component } from "solid-js";
import { createMemo, createSignal, getOwner, runWithOwner, Show } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import { useNavigate } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";
import SeparatorLine from "../../../components/SeparatorLine";
import { useTranslation } from "../../../state/i18n";
import { useModalManager } from "../../../components/modal/ModalManager";
import { createForm } from "../../../components/form/createForm";
import { pick } from "valibot";
import { customerSchema } from "../../../types/Customer";
import { CUSTOMER_DELETION_DELAY_DAYS, MINIMUM_ACTION_LOADING_DELAY } from "../../../constants";
import pMinDelay from "p-min-delay";
import GenericFailureModal from "../../GenericFailureModal";
import type { DateFormat } from "../../../state/customer";
import { useCustomerState } from "../../../state/customer";
import { TextButton } from "../../../components/button/Button";
import { SingleSelectInput } from "../../../components/menu/Select";
import CustomerResourceInput from "../../../fields/CustomerResourceInput";
import ApiService from "../../../services/ApiService";
import { isNil } from "lodash-es";
import { Translation } from "../../../utils/Translation";
import TextLink from "../../../components/link/TextLink";
import { keyFromSelector } from "i18next";
import { WarningModal } from "../../../components/modal/Modal";
import CustomerDeletionModal from "./CustomerDeletionModal";
import SettingSection from "./components/SettingSection.tsx";
import { EditableSettingTextValue } from "./components/SettingValue.tsx";
import SettingSectionEditActions from "./components/SettingSectionEditActions.tsx";
import SettingSectionEditButton from "./components/SettingSectionEditButton.tsx";
import BillingService from "../../../services/BillingService.ts";

const CompanyInformationPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_ACCOUNT_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_COMPANY_INFORMATION_LABEL)}
      />

      <div class="-mx-px flex h-full max-w-400 flex-col gap-6 overflow-y-auto px-px pt-8 pb-6">
        <InformationSection />
        <SeparatorLine />
        <SettingsSection />
        <SeparatorLine />

        <Show when={PermissionService.canManageCompanyInformation(customer.customerId)}>
          <DeletionSection />
          <SeparatorLine />
        </Show>
      </div>
    </div>
  );
};

const InformationSection = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const [isEditingCompanyInformation, setIsEditingCompanyInformation] = createSignal(false);
  const form = createForm(
    pick(customerSchema, ["name"]),
    async (value) =>
      pMinDelay(ApiService.updateCustomerName().mutateAsync(value.name), MINIMUM_ACTION_LOADING_DELAY)
        .then(() => setIsEditingCompanyInformation(false))
        .catch((err) =>
          open({
            content: () => <GenericFailureModal error={err} />,
          }),
        ),
    {
      name: customer.name,
    },
  );

  return (
    <SettingSection
      ariaIdPrefix="company-info"
      title={t(($) => $.SETTINGS_COMPANY_INFORMATION_SECTION_TITLE)}
      description={t(($) => $.SETTINGS_COMPANY_INFORMATION_SECTION_DESCRIPTION)}
    >
      <div class="flex min-w-0 flex-1 grow flex-col gap-6">
        <EditableSettingTextValue
          text={customer.name}
          label={t(($) => $.SETTINGS_COMPANY_INFORMATION_NAME)}
          editing={isEditingCompanyInformation()}
          editValue={form.getValue("name")}
          editError={form.getError("name")}
          editOnBlur={(value) => form.updateValue("name", value)}
          editDisabled={form.isSubmitting()}
          required
        />

        <SettingSectionEditActions
          form={form}
          editing={isEditingCompanyInformation()}
          stopEditing={() => setIsEditingCompanyInformation(false)}
          cancelLabel={t(($) => $.SETTINGS_COMPANY_INFORMATION_ACTIONS_CANCEL_LABEL)}
          saveLabel={t(($) => $.SETTINGS_COMPANY_INFORMATION_ACTIONS_SAVE_LABEL)}
          savingLabel={t(($) => $.SETTINGS_COMPANY_INFORMATION_ACTIONS_SAVING_LABEL)}
        />
      </div>

      <SettingSectionEditButton
        editing={isEditingCompanyInformation()}
        editLabel={t(($) => $.SETTINGS_COMPANY_INFORMATION_ACTIONS_EDIT_LABEL)}
        setEditing={() => setIsEditingCompanyInformation(true)}
      />
    </SettingSection>
  );
};

const SettingsSection = () => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();

  return (
    <SettingSection
      ariaIdPrefix="company-settings"
      title={t(($) => $.SETTINGS_COMPANY_SETTINGS_SECTION_TITLE)}
      description={t(($) => $.SETTINGS_COMPANY_SETTINGS_SECTION_DESCRIPTION)}
      col
    >
      <CustomerResourceInput
        label={t(($) => $.SETTINGS_COMPANY_SETTINGS_PDF_LOGO)}
        buttonLabel={t(($) => $.SETTINGS_COMPANY_SETTINGS_PDF_LOGO_ACTION_UPLOAD)}
        value={customer.logoAssetId ?? null}
        fixedSize
        onSelect={(value) => {
          runWithOwner(owner, () => {
            ApiService.updateCustomerLogo().mutate(value.id);
          });
        }}
        onClear={() => {
          runWithOwner(owner, () => {
            ApiService.removeCustomerLogo().mutate(undefined, {
              onError: (err) => {
                open({
                  content: () => <GenericFailureModal error={err} />,
                });
              },
            });
          });
        }}
      />

      <SingleSelectInput<DateFormat>
        label={t(($) => $.SETTINGS_COMPANY_SETTINGS_DATE_FORMAT)}
        value={customer.settings.dateFormat}
        options={[
          { variant: "text", value: "DDMMYYYY", label: "dd/mm/yyyy" },
          { variant: "text", value: "MMDDYYYY", label: "mm/dd/yyyy" },
          { variant: "text", value: "YYYYMMDD", label: "yyyy/mm/dd" },
        ]}
        onSelect={(value) => {
          runWithOwner(owner, () => {
            ApiService.updateCustomerSettings().mutate(
              {
                dateFormat: value,
                segment: customer.settings.segment,
                otherSegment: customer.settings.otherSegment,
              },
              {
                onError: (err) => {
                  open({
                    content: () => <GenericFailureModal error={err} />,
                  });
                },
              },
            );
          });
        }}
        sameWidth
      />
    </SettingSection>
  );
};

const DeletionSection = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const navigate = useNavigate();
  const hasDeletionScheduled = createMemo(() => !isNil(customer.meta.deleteOn));
  const subscriptionStatusQuery = BillingService.getSubscriptionStatus();

  return (
    <SettingSection
      ariaIdPrefix="deletion"
      title={t(($) => $.SETTINGS_COMPANY_DELETION_SECTION_TITLE)}
      description={
        <Show
          when={!hasDeletionScheduled()}
          fallback={t(($) => $.SETTINGS_COMPANY_DELETION_SCHEDULED, {
            deleteOn: customer.meta.deleteOn,
            formatParams: { deleteOn: { dateStyle: "long" } },
          })}
        >
          <Translation
            key={keyFromSelector(($) => $.SETTINGS_COMPANY_DELETION_SECTION_DESCRIPTION)}
            components={{
              externalLink: (props) => <TextLink isExternal href={props.href} label={props.label} />,
            }}
          />
        </Show>
      }
    >
      <TextButton
        variant="secondary"
        color="destructive"
        size="large"
        class="ml-auto"
        disabled={hasDeletionScheduled() || !subscriptionStatusQuery.isFetched}
        text={t(($) => $.SETTINGS_COMPANY_DELETION_ACTION_DELETE)}
        onClick={() => {
          if (isNil(subscriptionStatusQuery.data)) {
            return;
          }

          if (subscriptionStatusQuery.data.unpaidInvoices) {
            open({
              content: () => (
                <WarningModal
                  title={t(($) => $.SETTINGS_COMPANY_DELETION_OPEN_INVOICES_MODAL_TITLE)}
                  description={t(($) => $.SETTINGS_COMPANY_DELETION_OPEN_INVOICES_MODAL_DESCRIPTION)}
                  cancelLabel={t(($) => $.SETTINGS_COMPANY_DELETION_OPEN_INVOICES_MODAL_ACTIONS_CANCEL)}
                  confirmLabel={t(($) => $.SETTINGS_COMPANY_DELETION_OPEN_INVOICES_MODAL_ACTIONS_CONFIRM)}
                  onConfirm={() => {
                    navigate(`/customers/${customer.customerId}/settings/invoices`);
                  }}
                />
              ),
            });
          } else {
            const deleteOn = new Date();
            deleteOn.setUTCDate(deleteOn.getUTCDate() + CUSTOMER_DELETION_DELAY_DAYS);

            open({ content: () => <CustomerDeletionModal deleteOn={deleteOn} /> });
          }
        }}
      />
    </SettingSection>
  );
};

const ProtectedCompanyInformationPage = withPagePermissionCheck(
  CompanyInformationPage,
  PermissionService.canManageCompanyInformation,
);

export default ProtectedCompanyInformationPage;
